/**
 * The Market screen's charts (docs/market-ux.md §4), inline SVG in the same manner as
 * PriceHistoryPanel: recessive hairline grid, 2px lines, step-after (a quote holds until the next
 * one replaces it), ≥8px markers with a surface ring, a legend for two or more series (text in ink
 * tokens; the colour rides the swatch), and a crosshair + tooltip on hover / touch.
 *
 *   TimeChart   time × value: venue lines (Price, Liquidity), the consensus reference (dashed),
 *               your fills (dots), the venue spread band and the best-venue step line (Best)
 *   DepthChart  price × cumulative size, one step area per side (the "mountain")
 *
 * Venue colours come from lib/venueColors.ts (validated against the panel surface), so a venue is
 * the same colour on every tab and in the order book's dots.
 */
import { useMemo, useRef, useState, type PointerEvent, type ReactNode } from "react";
import { useIsMobile } from "../../lib/responsive.js";

export interface ChartLine {
  id: string;
  label: string;
  color: string;
  points: { t: number; v: number }[];
}

export interface ChartRef {
  label: string;
  v: number;
  color: string;
}

export interface ChartDot {
  t: number;
  v: number;
  label: string;
}

export interface BestSeg {
  t: number;
  v: number;
  id: string;
  label: string;
  color: string;
}

const SURFACE = "var(--color-panel-2)";
const NEUTRAL = "#8b93a7";

function useGeom(height: number) {
  const mobile = useIsMobile();
  const W = mobile ? 380 : 560;
  const PAD = mobile ? { l: 40, r: 10, t: 10, b: 22 } : { l: 44, r: 12, t: 10, b: 22 };
  return { W, H: height, PAD, fs: mobile ? 11 : 10 };
}

/** Swatch + label per series (≥2). */
export function Legend({ items }: { items: { id: string; label: string; color: string; dashed?: boolean; dot?: boolean }[] }) {
  if (items.length < 2) return null;
  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-dim" aria-label="Legend">
      {items.map((it) => (
        <li key={it.id} className="flex items-center gap-1.5">
          {it.dot ? (
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: it.color }} aria-hidden />
          ) : (
            <span
              className="inline-block h-0 w-3"
              style={{ borderTop: `2px ${it.dashed ? "dashed" : "solid"} ${it.color}` }}
              aria-hidden
            />
          )}
          <span>{it.label}</span>
        </li>
      ))}
    </ul>
  );
}

/** The value a step series holds at t (its last point at or before t). */
function at(points: { t: number; v: number }[], t: number): { t: number; v: number } | null {
  let r: { t: number; v: number } | null = null;
  for (const p of points) {
    if (p.t > t) break;
    r = p;
  }
  return r;
}

function stepD(pts: { t: number; v: number }[], x: (t: number) => number, y: (v: number) => number, tEnd: number): string {
  if (!pts.length) return "";
  let d = `M${x(pts[0].t).toFixed(1)} ${y(pts[0].v).toFixed(1)}`;
  for (let i = 1; i < pts.length; i++) d += ` H${x(pts[i].t).toFixed(1)} V${y(pts[i].v).toFixed(1)}`;
  return `${d} H${x(tEnd).toFixed(1)}`;
}

const timeFmt = (t: number, spanMs: number) =>
  spanMs > 36 * 3_600_000
    ? new Date(t).toLocaleDateString([], { month: "short", day: "numeric" })
    : new Date(t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/**
 * A time chart over [t0, t1]. Lines step and hold to t1 (the feed only sends changes).
 */
export function TimeChart({
  t0,
  t1,
  lines = [],
  refs = [],
  dots = [],
  band = [],
  best = [],
  fmt,
  height = 220,
  label,
  floor0 = false,
}: {
  t0: number;
  t1: number;
  lines?: ChartLine[];
  refs?: ChartRef[];
  dots?: ChartDot[];
  band?: { t: number; lo: number; hi: number }[];
  best?: BestSeg[];
  fmt: (v: number) => string;
  height?: number;
  label: string;
  /** Start the y axis at 0 (sizes). */
  floor0?: boolean;
}) {
  const { W, H, PAD, fs } = useGeom(height);
  const svgRef = useRef<SVGSVGElement>(null);
  const [cursor, setCursor] = useState<number | null>(null);

  const [v0, v1] = useMemo(() => {
    const vs: number[] = [];
    for (const l of lines) for (const p of l.points) vs.push(p.v);
    for (const r of refs) vs.push(r.v);
    for (const d of dots) vs.push(d.v);
    for (const b of band) vs.push(b.lo, b.hi);
    for (const b of best) vs.push(b.v);
    if (!vs.length) return [0, 1];
    let lo = Math.min(...vs);
    let hi = Math.max(...vs);
    const pad = hi > lo ? (hi - lo) * 0.12 : Math.max(Math.abs(hi) * 0.05, 0.01);
    lo = floor0 ? 0 : lo - pad;
    hi = hi + pad;
    return [lo, hi];
  }, [lines, refs, dots, band, best, floor0]);

  const x = (t: number) => PAD.l + ((Math.min(t1, Math.max(t0, t)) - t0) / (t1 - t0 || 1)) * (W - PAD.l - PAD.r);
  const y = (v: number) => PAD.t + (1 - (v - v0) / (v1 - v0 || 1)) * (H - PAD.t - PAD.b);
  const yTicks = [0, 1, 2, 3].map((i) => v0 + ((v1 - v0) * i) / 3);
  const xTicks = [0, 1, 2, 3].map((i) => t0 + ((t1 - t0) * i) / 3);

  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return;
    const fx = ((e.clientX - r.left) / r.width) * W;
    setCursor(Math.min(t1, Math.max(t0, t0 + ((fx - PAD.l) / (W - PAD.l - PAD.r)) * (t1 - t0))));
  };

  // Clip to the plot: points before t0 hold into the window from its left edge.
  const clip = (pts: { t: number; v: number }[]) => {
    const before = pts.filter((p) => p.t < t0).at(-1);
    const inside = pts.filter((p) => p.t >= t0 && p.t <= t1);
    return before ? [{ t: t0, v: before.v }, ...inside] : inside;
  };

  const hover = useMemo(() => {
    if (cursor == null) return null;
    const rows: { key: string; label: string; color: string; v: number; dashed?: boolean }[] = [];
    if (best.length) {
      const b = at(best.map((s) => ({ t: s.t, v: s.v })), cursor);
      const seg = b ? best.find((s) => s.t === b.t) : null;
      if (seg) rows.push({ key: "best", label: `Best: ${seg.label}`, color: seg.color, v: seg.v });
      const bd = at(band.map((s) => ({ t: s.t, v: s.lo })), cursor);
      const bb = bd ? band.find((s) => s.t === bd.t) : null;
      if (bb) rows.push({ key: "gap", label: `Venues apart (${fmt(bb.lo)}–${fmt(bb.hi)})`, color: NEUTRAL, v: bb.hi - bb.lo });
    }
    for (const l of lines) {
      const p = at(l.points, cursor);
      if (p) rows.push({ key: l.id, label: l.label, color: l.color, v: p.v });
    }
    if (!best.length) rows.sort((a, b) => a.v - b.v);
    for (const r of refs) rows.push({ key: `ref-${r.label}`, label: r.label, color: r.color, v: r.v, dashed: true });
    return rows.length ? { t: cursor, rows } : null;
  }, [cursor, lines, refs, best, band, fmt]);

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="block w-full touch-pan-y select-none"
        onPointerMove={onMove}
        onPointerDown={onMove}
        onPointerLeave={() => setCursor(null)}
        role="img"
        aria-label={label}
      >
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} stroke="currentColor" strokeOpacity={0.08} strokeWidth={1} vectorEffect="non-scaling-stroke" />
            <text x={PAD.l - 6} y={y(v) + 3} textAnchor="end" fontSize={fs} fill="currentColor" fillOpacity={0.45} className="nums">
              {fmt(v)}
            </text>
          </g>
        ))}
        {xTicks.map((t, i) => (
          <text key={i} x={x(t)} y={H - 6} textAnchor={i === 0 ? "start" : i === 3 ? "end" : "middle"} fontSize={fs} fill="currentColor" fillOpacity={0.45} className="nums">
            {timeFmt(t, t1 - t0)}
          </text>
        ))}

        {/* the spread band: max − min across venues, a neutral wash */}
        {band.length > 0 && <BandPath band={band} x={x} y={y} t0={t0} t1={t1} />}

        {refs.map((r) => (
          <line key={r.label} x1={PAD.l} x2={W - PAD.r} y1={y(r.v)} y2={y(r.v)} stroke={r.color} strokeWidth={1.5} strokeDasharray="5 4" vectorEffect="non-scaling-stroke" />
        ))}

        {lines.map((l) => {
          const pts = clip(l.points);
          if (!pts.length) return null;
          const last = pts[pts.length - 1];
          return (
            <g key={l.id}>
              <path d={stepD(pts, x, y, t1)} fill="none" stroke={l.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              <circle cx={x(t1)} cy={y(last.v)} r={3} fill={l.color} stroke={SURFACE} strokeWidth={1.5} />
            </g>
          );
        })}

        {/* best venue over time: each run coloured by the venue that led it */}
        {best.length > 0 &&
          (() => {
            const segs = best.filter((s) => s.t <= t1);
            return segs.map((s, i) => {
              const end = segs[i + 1]?.t ?? t1;
              if (end < t0) return null;
              const nx = segs[i + 1];
              return (
                <g key={`${s.t}-${i}`}>
                  <line x1={x(s.t)} x2={x(end)} y1={y(s.v)} y2={y(s.v)} stroke={s.color} strokeWidth={2.5} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                  {nx && <line x1={x(end)} x2={x(end)} y1={y(s.v)} y2={y(nx.v)} stroke={NEUTRAL} strokeOpacity={0.5} strokeWidth={1} vectorEffect="non-scaling-stroke" />}
                </g>
              );
            });
          })()}

        {dots
          .filter((d) => d.t >= t0 && d.t <= t1)
          .map((d, i) => (
            <circle key={i} cx={x(d.t)} cy={y(d.v)} r={4.5} fill="var(--color-amber)" stroke={SURFACE} strokeWidth={2}>
              <title>{d.label}</title>
            </circle>
          ))}

        {hover && (
          <line x1={x(hover.t)} x2={x(hover.t)} y1={PAD.t} y2={H - PAD.b} stroke="currentColor" strokeOpacity={0.3} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        )}
      </svg>

      {hover && (
        <Tip left={(x(hover.t) / W) * 100}>
          <div className="nums mb-1 text-[10px] text-faint">{new Date(hover.t).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
          {hover.rows.map((r) => (
            <div key={r.key} className="flex items-center gap-1.5 py-0.5 text-[11px]">
              <span className="inline-block h-0 w-2.5 shrink-0" style={{ borderTop: `2px ${r.dashed ? "dashed" : "solid"} ${r.color}` }} aria-hidden />
              <span className="min-w-0 truncate text-dim">{r.label}</span>
              <span className="nums ml-auto pl-2 text-ink">{fmt(r.v)}</span>
            </div>
          ))}
        </Tip>
      )}
    </div>
  );
}

function BandPath({ band, x, y, t0, t1 }: { band: { t: number; lo: number; hi: number }[]; x: (t: number) => number; y: (v: number) => number; t0: number; t1: number }) {
  const pts = band.filter((b) => b.t <= t1);
  if (!pts.length) return null;
  // Step-after upper edge left→right, then the lower edge back.
  let top = "";
  let bot = "";
  pts.forEach((b, i) => {
    const xs = x(Math.max(t0, b.t));
    const xe = x(pts[i + 1]?.t ?? t1);
    top += `${i === 0 ? "M" : "L"}${xs.toFixed(1)} ${y(b.hi).toFixed(1)} L${xe.toFixed(1)} ${y(b.hi).toFixed(1)} `;
  });
  for (let i = pts.length - 1; i >= 0; i--) {
    const b = pts[i];
    const xs = x(Math.max(t0, b.t));
    const xe = x(pts[i + 1]?.t ?? t1);
    bot += `L${xe.toFixed(1)} ${y(b.lo).toFixed(1)} L${xs.toFixed(1)} ${y(b.lo).toFixed(1)} `;
  }
  return <path d={`${top}${bot}Z`} fill={NEUTRAL} fillOpacity={0.16} stroke="none" />;
}

function Tip({ left, children }: { left: number; children: ReactNode }) {
  return (
    <div
      className="pointer-events-none absolute top-1 z-10 w-[11rem] rounded-md border border-border-2 bg-panel/95 px-2 py-1.5 shadow-lg backdrop-blur"
      style={left > 55 ? { right: `${Math.max(2, 100 - left + 2)}%` } : { left: `${left + 2}%` }}
    >
      {children}
    </div>
  );
}

/* ================================================================ depth */

export interface DepthSide {
  id: string;
  label: string;
  color: string;
  /** Cheapest first, cumulative. */
  curve: { price: number; cum: number }[];
}

/** Cumulative size against price for each side: the walk from the best price up the book. */
export function DepthChart({ sides, fmtPrice, fmtSize, height = 220 }: { sides: DepthSide[]; fmtPrice: (p: number) => string; fmtSize: (n: number) => string; height?: number }) {
  const { W, H, PAD, fs } = useGeom(height);
  const svgRef = useRef<SVGSVGElement>(null);
  const [cursor, setCursor] = useState<number | null>(null);
  const shown = sides.filter((s) => s.curve.length > 0);
  const [p0, p1, c1] = useMemo(() => {
    const ps = shown.flatMap((s) => s.curve.map((c) => c.price));
    const cs = shown.flatMap((s) => s.curve.map((c) => c.cum));
    if (!ps.length) return [0, 1, 1];
    const lo = Math.min(...ps);
    const hi = Math.max(...ps);
    const pad = hi > lo ? (hi - lo) * 0.04 : 0.01;
    return [Math.max(0, lo - pad), Math.min(1, hi + pad), Math.max(...cs) * 1.08 || 1];
  }, [shown]);
  const x = (p: number) => PAD.l + ((p - p0) / (p1 - p0 || 1)) * (W - PAD.l - PAD.r);
  const y = (c: number) => PAD.t + (1 - c / c1) * (H - PAD.t - PAD.b);
  const base = y(0);
  const xTicks = [0, 1, 2, 3, 4].map((i) => p0 + ((p1 - p0) * i) / 4);
  const yTicks = [0, 1, 2, 3].map((i) => (c1 * i) / 3);

  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return;
    const fx = ((e.clientX - r.left) / r.width) * W;
    setCursor(Math.min(p1, Math.max(p0, p0 + ((fx - PAD.l) / (W - PAD.l - PAD.r)) * (p1 - p0))));
  };
  const hover =
    cursor == null
      ? null
      : shown
          .map((s) => {
            const lv = s.curve.filter((c) => c.price <= cursor + 1e-9).at(-1);
            return lv ? { id: s.id, label: s.label, color: s.color, cum: lv.cum } : null;
          })
          .filter((r): r is NonNullable<typeof r> => !!r);

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="block w-full touch-pan-y select-none"
        onPointerMove={onMove}
        onPointerDown={onMove}
        onPointerLeave={() => setCursor(null)}
        role="img"
        aria-label="Order book depth: cumulative size by price, each side"
      >
        {yTicks.map((c, i) => (
          <g key={i}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(c)} y2={y(c)} stroke="currentColor" strokeOpacity={0.08} strokeWidth={1} vectorEffect="non-scaling-stroke" />
            <text x={PAD.l - 6} y={y(c) + 3} textAnchor="end" fontSize={fs} fill="currentColor" fillOpacity={0.45} className="nums">
              {fmtSize(c)}
            </text>
          </g>
        ))}
        {xTicks.map((p, i) => (
          <text key={i} x={x(p)} y={H - 6} textAnchor={i === 0 ? "start" : i === 4 ? "end" : "middle"} fontSize={fs} fill="currentColor" fillOpacity={0.45} className="nums">
            {fmtPrice(p)}
          </text>
        ))}
        {shown.map((s) => {
          let d = `M${x(s.curve[0].price).toFixed(1)} ${base.toFixed(1)}`;
          for (const c of s.curve) d += ` H${x(c.price).toFixed(1)} V${y(c.cum).toFixed(1)}`;
          const line = d;
          return (
            <g key={s.id}>
              <path d={`${line} V${base.toFixed(1)} Z`} fill={s.color} fillOpacity={0.12} stroke="none" />
              <path d={line} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            </g>
          );
        })}
        {cursor != null && <line x1={x(cursor)} x2={x(cursor)} y1={PAD.t} y2={H - PAD.b} stroke="currentColor" strokeOpacity={0.3} strokeWidth={1} vectorEffect="non-scaling-stroke" />}
      </svg>
      {cursor != null && hover && hover.length > 0 && (
        <Tip left={(x(cursor) / W) * 100}>
          <div className="nums mb-1 text-[10px] text-faint">up to {fmtPrice(cursor)}</div>
          {hover.map((r) => (
            <div key={r.id} className="flex items-center gap-1.5 py-0.5 text-[11px]">
              <span className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ background: r.color }} aria-hidden />
              <span className="min-w-0 truncate text-dim">{r.label}</span>
              <span className="nums ml-auto pl-2 text-ink">{fmtSize(r.cum)}</span>
            </div>
          ))}
        </Tip>
      )}
    </div>
  );
}
